  (async () => {
    'use strict';

    const ROUTER_DATA_NODE = document.getElementById('zpt-domain-router-data');
    const ROUTER_DATA = ROUTER_DATA_NODE
      ? JSON.parse(ROUTER_DATA_NODE.dataset.config || '{}')
      : {};

    const CONFIG = Object.freeze({
      previewVerificationMs: 1000,
      paths: Object.freeze({
        notFound: '/404',
        productsNamespace: '/products',
        productsPrefix: '/products/'
      }),
      storefrontBlockedPaths: Object.freeze(['/blogs']),
      standaloneAllowedPaths: Object.freeze([
        '/cart', '/checkout', '/checkouts', '/challenge', '/account', '/policies', '/pages'
      ])
    });

    const STOREFRONTS = Array.isArray(ROUTER_DATA.storefronts) ? ROUTER_DATA.storefronts : [];
    const PRODUCT_ROUTES = Array.isArray(ROUTER_DATA.productRoutes) ? ROUTER_DATA.productRoutes : [];

    /*
     * ======================================================
     * NORMALIZATION
     * ======================================================
     */

    const normalizeHost = value =>
      String(value || '')
        .trim()
        .toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .replace(/\/.*$/, '')
        .replace(/\/+$/, '');

    const normalizeKey = value =>
      String(value || '')
        .trim()
        .toLowerCase();

    const normalizeHandle = value => {
      try {
        return decodeURIComponent(
          String(value || '')
        )
          .trim()
          .toLowerCase();
      } catch {
        return String(value || '')
          .trim()
          .toLowerCase();
      }
    };

    const normalizePath = value => {
      const path =
        String(value || '/')
          .replace(/\/{2,}/g, '/');

      if (
        path.length > 1 &&
        path.endsWith('/')
      ) {
        return path.slice(0, -1);
      }

      return path || '/';
    };

    const host =
      normalizeHost(
        ROUTER_DATA.requestHost
      );

    const permanentDomain =
      normalizeHost(
        ROUTER_DATA.permanentDomain
      );

    const isPermanentShopHost =
      Boolean(permanentDomain) &&
      host === permanentDomain;

    const pathname =
      normalizePath(
        window.location.pathname
      );

    const origin =
      window.location.origin;

    const params =
      new URLSearchParams(
        window.location.search
      );

    let isPreview =
      params.has('preview_theme_id') ||
      ROUTER_DATA.designMode ||
      ROUTER_DATA.visualPreviewMode;

    /*
     * ======================================================
     * STOREFRONT LOOKUPS
     * ======================================================
     */

    const getStoreHosts = store => {
      const hosts = [];

      const domain =
        normalizeHost(
          store?.domain
        );

      if (domain) {
        hosts.push(domain);
      }

      if (
        Array.isArray(
          store?.hostnames
        )
      ) {
        for (
          const alias
          of store.hostnames
        ) {
          const normalized =
            normalizeHost(alias);

          if (
            normalized &&
            !hosts.includes(normalized)
          ) {
            hosts.push(normalized);
          }
        }
      }

      return hosts;
    };

    const findStoreByKey = key =>
      STOREFRONTS.find(
        store =>
          normalizeKey(store.key) ===
          normalizeKey(key)
      ) || null;

    const findExactStoreByHost =
      hostname => {
        const target =
          normalizeHost(hostname);

        return (
          STOREFRONTS.find(
            store =>
              store.enabled !== false &&
              getStoreHosts(store)
                .includes(target)
          ) ||
          null
        );
      };

    const findProductRoute =
      hostname => {
        const target =
          normalizeHost(hostname);

        return (
          PRODUCT_ROUTES.find(
            route =>
              route.enabled !== false &&
              route.handle &&
              normalizeHost(
                route.hostname
              ) === target
          ) ||
          null
        );
      };

    /*
     * ======================================================
     * PRODUCT ROUTE MEMBERSHIP
     * ======================================================
     */

    const getProductHandleFromPath =
      path => {
        const normalizedPath =
          normalizePath(path);

        if (
          !normalizedPath.startsWith(
            CONFIG.paths.productsPrefix
          )
        ) {
          return null;
        }

        const remainder =
          normalizedPath.slice(
            CONFIG.paths.productsPrefix.length
          );

        if (
          !remainder ||
          remainder.includes('/')
        ) {
          return null;
        }

        return normalizeHandle(
          remainder
        );
      };

    const findProductRouteForStorefront =
      (
        storeKey,
        productHandle
      ) => {
        const normalizedStoreKey =
          normalizeKey(storeKey);

        const normalizedProductHandle =
          normalizeHandle(productHandle);

        if (
          !normalizedStoreKey ||
          !normalizedProductHandle
        ) {
          return null;
        }

        const store = findStoreByKey(normalizedStoreKey);
        return store?.productHandles?.some(
          handle => normalizeHandle(handle) === normalizedProductHandle
        ) ? true : false;
      };

    const storefrontOwnsProductPath =
      (
        store,
        path
      ) => {
        if (!store?.key) {
          return false;
        }

        const productHandle =
          getProductHandleFromPath(
            path
          );

        if (!productHandle) {
          return false;
        }

        return findProductRouteForStorefront(
          store.key,
          productHandle
        );
      };

    /*
     * ======================================================
     * PATH HELPERS
     * ======================================================
     */

    const pathMatchesNamespace = (
      path,
      namespace
    ) => {
      const normalizedPath =
        normalizePath(path);

      const normalizedNamespace =
        normalizePath(namespace);

      return (
        normalizedPath ===
          normalizedNamespace ||
        normalizedPath.startsWith(
          normalizedNamespace + '/'
        )
      );
    };

    const isStorefrontPathBlocked =
      path =>
        CONFIG.storefrontBlockedPaths.some(
          blockedPath =>
            pathMatchesNamespace(
              path,
              blockedPath
            )
        );

    const isProductsNamespace =
      path =>
        pathMatchesNamespace(
          path,
          CONFIG.paths.productsNamespace
        );

    const isStandaloneAllowedPath =
      path =>
        CONFIG.standaloneAllowedPaths.some(
          allowedPath =>
            pathMatchesNamespace(
              path,
              allowedPath
            )
        );

    /*
     * ======================================================
     * STOREFRONT ACCESS
     * ======================================================
     */

    const canAccessStorefrontPath = (
      store,
      path
    ) => {
      const normalizedPath =
        normalizePath(path);

      if (
        normalizedPath ===
        CONFIG.paths.notFound
      ) {
        return true;
      }

      if (store) {
        if (
          isStorefrontPathBlocked(
            normalizedPath
          )
        ) {
          return false;
        }

        /*
         * Products must belong to this Storefront.
         */
        if (
          isProductsNamespace(
            normalizedPath
          )
        ) {
          return storefrontOwnsProductPath(
            store,
            normalizedPath
          );
        }

        return true;
      }

      return false;
    };

    /*
     * ======================================================
     * PRODUCT ROUTE ACCESS
     * ======================================================
     */

    const canAccessProductRoutePath = (
      parentStore,
      path,
      productPath
    ) => {
      const normalizedPath =
        normalizePath(path);

      const normalizedProductPath =
        normalizePath(productPath);

      /*
       * 404 is always available.
       */
      if (
        normalizedPath ===
        CONFIG.paths.notFound
      ) {
        return true;
      }

      /*
       * Every Product Route can always access
       * its own mapped product.
       */
      if (
        normalizedPath ===
        normalizedProductPath
      ) {
        return true;
      }

      /*
       * LINKED PRODUCT ROUTE
       *
       * Once a Product Route has a parent Storefront,
       * all other paths use that Storefront's policy.
       */
      if (parentStore) {
        return canAccessStorefrontPath(
          parentStore,
          normalizedPath
        );
      }

      /*
       * STANDALONE PRODUCT ROUTE
       *
       * It has no parent Storefront, but it still needs
       * Shopify commerce and customer utility routes.
       *
       * These routes stay on the standalone hostname.
       */
      if (
        isStandaloneAllowedPath(
          normalizedPath
        )
      ) {
        return true;
      }

      /*
       * Everything else is private to the standalone
       * microsite and therefore rejected.
       */
      return false;
    };

    /*
     * ======================================================
     * ROUTE DATA
     * ======================================================
     */

    const storeRouteData =
      store => ({
        storeKey:
          store?.key || null,

        storeDomain:
          store?.domain || null,

        storeName:
          store?.name || null,

        storeLogo:
          store?.logo || null,

        storeFavicon:
          store?.favicon || null,

        storeBanner:
          store?.banner || null
      });

    const makeRoute = (
      data = {}
    ) => ({
      mode:
        'unknown',

      host,

      preview:
        isPreview,

      productHandle:
        null,

      productPath:
        null,

      productRouteHostname:
        null,

      storeKey:
        null,

      storeDomain:
        null,

      storeName:
        null,

      storeLogo:
        null,

      storeFavicon:
        null,

      storeBanner:
        null,

      homeUrl:
        origin + '/',

      ...data
    });

    const exposeRoute =
      route => {
        route.preview =
          isPreview;

        window.StorefrontRouter =
          route;

        const html =
          document.documentElement;

        document.getElementById('zpt-router-render-gate')?.remove();

        html.style.visibility =
          '';

        html.dataset.storefrontMode =
          route.mode;

        html.dataset.storefrontManagerPreview =
          route.preview
            ? 'true'
            : 'false';

        const attributes = {
          storeKey:
            route.storeKey,

          storeDomain:
            route.storeDomain,

          storeName:
            route.storeName,

          productHandle:
            route.productHandle,

          productRouteHostname:
            route.productRouteHostname
        };

        for (
          const [name, value]
          of Object.entries(attributes)
        ) {
          if (value) {
            html.dataset[name] =
              value;
          } else {
            delete html.dataset[name];
          }
        }
      };

    const exposeEmptyUnknownHost = () => {
      const html = document.documentElement;
      const robots =
        document.querySelector('meta[name="robots"]') ||
        document.head.appendChild(
          Object.assign(document.createElement('meta'), {
            name: 'robots'
          })
        );

      robots.content = 'noindex, nofollow';
      document.title = '';
      document.body?.replaceChildren();

      window.StorefrontRouter = makeRoute({
        mode: 'unconfigured'
      });

      html.dataset.storefrontMode = 'unconfigured';
      html.dataset.storefrontManagerPreview = 'false';
      document.getElementById('zpt-router-render-gate')?.remove();
      html.style.visibility = '';
    };

    /*
     * ======================================================
     * NAVIGATION
     * ======================================================
     */

    const redirect = (
      target,
      preserveQuery = false
    ) => {
      document.documentElement
        .style.visibility =
          'hidden';

      const url =
        new URL(
          target,
          origin
        );

      if (preserveQuery) {
        url.search =
          window.location.search;
      }

      window.location.replace(
        url.toString()
      );
    };

    /*
     * ======================================================
     * PREVIEW DETECTION
     * ======================================================
     */

    const hasShopifyPreviewBar =
      () =>
        Boolean(
          document.getElementById(
            'preview-bar-iframe'
          ) ||
          document.getElementById(
            'PBarNextFrameWrapper'
          ) ||
          document.querySelector(
            'iframe[src*="shopifycloud/preview-bar"]'
          )
        );

    const waitForPreviewBar =
      () =>
        new Promise(
          resolve => {
            if (
              hasShopifyPreviewBar()
            ) {
              resolve(true);
              return;
            }

            let done = false;

            const observer =
              new MutationObserver(
                () => {
                  if (
                    hasShopifyPreviewBar()
                  ) {
                    finish(true);
                  }
                }
              );

            const finish =
              value => {
                if (done) return;

                done = true;

                observer.disconnect();

                window.clearTimeout(
                  timeout
                );

                resolve(value);
              };

            observer.observe(
              document.documentElement,
              {
                childList: true,
                subtree: true
              }
            );

            const timeout =
              window.setTimeout(
                () => {
                  finish(
                    hasShopifyPreviewBar()
                  );
                },
                CONFIG.previewVerificationMs
              );
          }
        );

    const confirmPreview =
      async () => {
        if (isPreview) {
          return true;
        }

        document.documentElement
          .style.visibility =
            'hidden';

        const previewBarExists =
          await waitForPreviewBar();

        if (!previewBarExists) {
          return false;
        }

        isPreview = true;

        document.documentElement
          .style.visibility =
            '';

        return true;
      };

    const allowRestrictedPath =
      async () => {
        if (isPreview) {
          return true;
        }

        return confirmPreview();
      };

    /*
     * ======================================================
     * 1. EXACT PRODUCT ROUTE
     * ======================================================
     */

    const productRoute =
      findProductRoute(host);

    if (
      productRoute &&
      productRoute.handle
    ) {
      /*
       * Storefront inheritance comes ONLY from the
       * Product Route's explicit Storefront relationship.
       *
       * A blank Storefront is valid and creates a
       * standalone Product Route.
       */
      const parentStore =
        productRoute.storeKey
          ? findStoreByKey(
              productRoute.storeKey
            )
          : null;

      const productPath =
        normalizePath(
          CONFIG.paths.productsPrefix +
          encodeURIComponent(
            productRoute.handle
          )
        );

      const productRouteData =
        makeRoute({
          mode:
            'microsite',

          productHandle:
            productRoute.handle,

          productPath,

          productRouteHostname:
            productRoute.hostname ||
            null,

          ...storeRouteData(
            parentStore
          ),

          homeUrl:
            parentStore?.domain
              ? 'https://' +
                normalizeHost(
                  parentStore.domain
                ) +
                '/'
              : origin + '/'
        });

      /*
       * Bare Product Route hostname always opens
       * that route's mapped product.
       *
       * Standalone:
       * product.example-store.test/
       * ->
       * product.example-store.test/products/{product}
       *
       * Linked:
       * campaign.example-store.test/
       * ->
       * campaign.example-store.test/products/{product}
       */
      if (pathname === '/') {
        redirect(
          productPath,
          true
        );

        return;
      }

      const pathAllowed =
        canAccessProductRoutePath(
          parentStore,
          pathname,
          productPath
        );

      if (pathAllowed) {
        exposeRoute(
          productRouteData
        );

        return;
      }

      /*
       * Restricted paths remain accessible while
       * editing/previewing the theme.
       */
      if (
        await allowRestrictedPath()
      ) {
        exposeRoute(
          productRouteData
        );

        return;
      }

      redirect(
        CONFIG.paths.notFound
      );

      return;
    }

    /*
     * ======================================================
     * 2. EXACT STOREFRONT
     * ======================================================
     */

    const exactStore =
      isPermanentShopHost && isPreview
        ? null
        : findExactStoreByHost(host);

    if (exactStore) {
      const storefrontRouteData =
        makeRoute({
          mode: 'storefront',

          ...storeRouteData(
            exactStore
          ),

          homeUrl:
            isPermanentShopHost
              ? origin + '/'
              : 'https://' +
                normalizeHost(exactStore.domain) +
                '/'
        });

      const pathAllowed =
        canAccessStorefrontPath(
          exactStore,
          pathname
        );

      if (!pathAllowed) {
        if (
          await allowRestrictedPath()
        ) {
          exposeRoute(
            storefrontRouteData
          );

          return;
        }

        redirect(
          CONFIG.paths.notFound
        );

        return;
      }

      exposeRoute(
        storefrontRouteData
      );

      return;
    }

    /*
     * ======================================================
     * 3. PREVIEW FALLBACK
     * ======================================================
     */

    if (await confirmPreview()) {
      exposeRoute(
        makeRoute({
          mode: 'preview',
          homeUrl:
            origin + '/'
        })
      );

      return;
    }

    /*
     * ======================================================
     * 4. UNKNOWN HOST
     * ======================================================
     */

    exposeEmptyUnknownHost();
  })();
